import Http from "./Http";
import AuthCredentials from "../login/AuthCredentials";
import { ReviewDbDto } from "../common/dto/ReviewDbDto";
import { ReviewFileCommentDbDto } from "../common/dto/ReviewFileCommentDbDto";
import ReviewQuestionApiDto from "../common/dto/ReviewQuestionApiDto";
import { ReviewConflictApiDto } from "../common/dto/ReviewConflictApiDto";
import { ReviewConflictResolutionApiDto } from "../common/dto/ReviewConflictResolutionApiDto";
import { CodeSnippetApiDto } from "../common/dto/CodeSnippetApiDto";
import { ReviewCommentRowApiDto } from "../common/dto/ReviewCommentRowApiDto";
import { DbInfoApiDto } from "../common/dto/DbInfoApiDto";
import UserResult from "../common/UserResult";
import { TagMatchDto } from "../common/dto/TagMatchDto";
import { BlockMatchSnippetDto } from "../common/dto/BlockMatchSnippetDto";
import { TagMatchResultDto } from "../common/dto/TagMatchResultDto";
import UserResultsFilter from "../common/filter/CourseResultsFilter";
import { PreExamEventEntry } from "../common/TestInfo";
import { UiSettings } from "../common/UiSettings";
import ProjectResultsFilter from "../common/filter/ProjectResultsFilter";
import { RegisteredUserRow } from "../common/dto/RegisteredUserRow";

export default class Dao {

    constructor(private readonly http: Http) {
    }

    applyDeclarationNames(): Promise<string[]> {
        return this.http.post('api/apply-declaration-names');
    }

    updateDeclarationNames(dataAsCsv: string) {
        return this.http.post('api/update-declaration-names', { dataAsCsv });
    }

    updateTestResults(testId: string, dataAsJson: string) {
        if (!['test1', 'test2', 'exam'].includes(testId)) {
            throw new Error('Invalid testId: ' + testId);
        }

        return this.http.post('api/test-results/' + testId, { dataAsJson });
    }

    validateRequirements(namesAsCsv: string) {
        return this.http.post('api/validate-requirements', { namesAsCsv });
    }

    confirmTestResults() {
        return this.http.post('api/confirm-test-results');
    }

    getCourseResults(filter: UserResultsFilter): Promise<UserResult[]> {
        return this.http.get('api/course-results?' + filter.asQueryString())
            .then(userResults => userResults.map(each => new UserResult().readRawData(each)));
    }

    getProjectResults(filter: ProjectResultsFilter): Promise<UserResult[]> {
        return this.http.get('api/results?' + filter.asQueryString())
            .then(userResults => userResults.map(each => new UserResult().readRawData(each)));
    }

    getProjectResultFor(userName: string): Promise<UserResult> {
        return this.http.get('api/results/' + userName)
            .then(data => new UserResult().readRawData(data));
    }

    getOutputLog(userName: string, tag: string): Promise<string> {
        return this.http.get(`api/output-log/${userName}/${tag}`)
            .then(data => data as string);
    }

    findReview(reviewId: string): Promise<ReviewDbDto> {
        return this.http.get(`api/review/${reviewId}`)
            .then(data => data as ReviewDbDto);
    }

    saveReview(review: ReviewDbDto): Promise<void> {
        return this.http.post(`api/review`, review);
    }

    findReviewsAssignedTo(username: string): Promise<ReviewDbDto[]> {
        return this.http.get(`api/reviews-assigned-to/${username}`)
            .then(data => data as ReviewDbDto[]);
    }

    findReviewFile(reviewId: string, fileIndex: number): Promise<string> {
        return this.http.get(`api/review-file/${reviewId}/${fileIndex}`);
    }

    findReviewFileList(reviewId: string): Promise<string[]> {
        return this.http.get(`api/review-file-list/${reviewId}`);
    }

    saveReviewFileComment(reviewId: string, comment: ReviewFileCommentDbDto): Promise<string> {
        return this.http.post(`api/review-file-comment/${reviewId}`, comment);
    }

    deleteReviewFileComment(commentId: string): Promise<void> {
        return this.http.delete(`api/review-file-comment/${commentId}`)
            .then(() => Promise.resolve());
    }

    findReviewFileComments(reviewId: string): Promise<ReviewFileCommentDbDto[]> {
        return this.http.get(`api/review-file-comment/${reviewId}`);
    }

    findFirstReviewConflict(): Promise<ReviewConflictApiDto> {
        return this.http.get(`api/first-review-conflict`)
            .catch(() => Promise.resolve());
    }

    findReviewCodeSnippetsForUserTag(username: string, tag: string): Promise<CodeSnippetApiDto[]> {
        return this.http.get(`api/review-code-snippets/${username}/${tag}`);
    }

    findReviewCommentRows(username: string): Promise<ReviewCommentRowApiDto[]> {
        return this.http.get(`api/review-comment-rows/${username}`);
    }

    resolveReviewConflict(resolution: ReviewConflictResolutionApiDto): Promise<void> {
        return this.http.post(`api/resolve-conflict`, resolution);
    }

    findReviewQuestions(tag: string): Promise<ReviewQuestionApiDto[]> {
        return this.http.get(`api/review-question/${tag}`);
    }

    findDbPassForUser(username: string): Promise<DbInfoApiDto> {
        return this.http.get(`api/db-pass/${username}`);
    }

    createDbForUser(username: string): Promise<DbInfoApiDto> {
        return this.http.post(`api/db-create/${username}`);
    }

    findDbPasses(): Promise<DbInfoApiDto[]> {
        return this.http.get(`api/db-passes`);
    }

    login(userName: string, password: string): Promise<AuthCredentials> {
        return this.http.post('api/login', { userName, password })
            .then(data => new AuthCredentials(userName, data.token ));
    }

    getCourseResultsUpdateScript(filter: UserResultsFilter) {
        return this.http.get('api/course-results-script?' + filter.asQueryString());
    }

    getNameToUsernameCsv() {
        return this.http.get('api/name-to-username-csv');
    }

    processUserTag(username: string, tag: string): Promise<string> {
        return this.http.post('api/add-user', { username, tag });
    }

    setFormOfStudy(username: string, formOfStudy: string | undefined): Promise<string> {
        return this.http.post('api/set-form-of-study', { username, formOfStudy });
    }

    createSampleReview(tag: string): Promise<string> {
        return this.http.post('api/create-sample-review', { tag });
    }

    findTagMatches(tag: string): Promise<TagMatchResultDto> {
        return this.http.get(`api/tag-matches/${tag}`);
    }

    findTagMatchById(tagMatchId: string): Promise<TagMatchDto> {
        return this.http.get(`api/tag-match-by-id/${tagMatchId}`);
    }

    findCodeMatchSnippet(tagMatchId: string,
                         blockIndex: number,
                         userIndex: number): Promise<BlockMatchSnippetDto> {

        return this.http.get(`api/block-matches/${tagMatchId}/${blockIndex}/${userIndex}`);
    }

    markCodeMatchAsProblem(tagMatchId: string): Promise<void> {
        return this.http.post(`api/mark-code-match-problem/${tagMatchId}`);
    }

    markCodeMatchAsCommon(hash: string): Promise<void> {
        return this.http.post(`api/delete-code-match-by-hash/${hash}`);
    }

    markCodeMatchFileAsIgnored(path: string): Promise<void> {
        return this.http.post(`api/mark-code-match-file-as-ignored`, {path});
    }

    deleteCodeMatchByTag(tag: string): Promise<void> {
        return this.http.post(`api/delete-code-match-by-tag/${tag}`);
    }

    getPreExamEvents(username: string): Promise<PreExamEventEntry[]> {
        return this.http.get(`api/pre-exam-events/${username}`);
    }

    registerToPreExam(username: string, eventId: string): Promise<void> {
        return this.http.post(`api/register-to-pre-exam/${username}/${eventId}`);
    }

    cancelPreExamRegistration(username: string): Promise<void> {
        return this.http.post(`api/cancel-pre-exam-registration/${username}`);
    }

    cancelRegistrations(usernames: string[]): Promise<string[]> {
        return this.http.post('api/cancel-registrations', { usernames });
    }

    getRegistrationListCsv(eventId: string): Promise<string> {
        return this.http.get(`api/registered-list-csv/${eventId}`);
    }

    getRegisteredListForEvent(eventId: string): Promise<RegisteredUserRow[]> {
        return this.http.get(`api/registered-list/${eventId}`);
    }

    getUiSettings(username: string): Promise<UiSettings> {
        return this.http.get(`api/ui-settings/${username}`);
    }
}
